// /app/frontend/src/components/DistressBadge.jsx
//
// Compact label chip for a single distress signal (foreclosure / tax-delinquent /
// absentee owner). Used on Discover result cards, lead-detail screens, and the
// shared property report when the owner is hustler-tier with active filters.

import { Flame, AlertTriangle, Home, DoorClosed, FileText, Users } from "lucide-react";

const CONFIG = {
  foreclosure: {
    label: "Pre-Foreclosure",
    icon: Flame,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-800",
  },
  tax_delinquent: {
    label: "Tax-Delinquent",
    icon: AlertTriangle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-800",
  },
  absentee: {
    label: "Absentee Owner",
    icon: Home,
    bg: "bg-sky-50",
    border: "border-sky-200",
    text: "text-sky-800",
  },
  vacant: {
    label: "Vacant",
    icon: DoorClosed,
    bg: "bg-violet-50",
    border: "border-violet-200",
    text: "text-violet-800",
  },
  probate: {
    label: "Probate",
    icon: FileText,
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    text: "text-indigo-800",
  },
  tired_landlord: {
    label: "Tired Landlord",
    icon: Users,
    bg: "bg-rose-50",
    border: "border-rose-200",
    text: "text-rose-800",
  },
};

/**
 * @param {{ kind: "foreclosure"|"tax_delinquent"|"absentee", detail?: string, compact?: boolean }} props
 */
export function DistressBadge({ kind, detail, compact = false }) {
  const cfg = CONFIG[kind];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border ${cfg.bg} ${cfg.border} ${cfg.text} ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"} font-semibold`}
      data-testid={`distress-badge-${kind}`}
      title={detail || cfg.label}
    >
      <Icon size={compact ? 9 : 11} />
      {compact ? cfg.label.split(" ")[0].replace("-", "") : cfg.label}
      {detail && !compact && <span className="opacity-70">· {detail}</span>}
    </span>
  );
}

/**
 * Render every distress badge that applies for a given signal object.
 * Pass the `distress` block returned from /api/distress/signal or
 * /api/discover/search.
 *
 * @param {{ signal: {absentee:boolean, foreclosure:boolean, foreclosure_stage?:string, tax_delinquent:boolean, tax_delinquent_amount?:number}, compact?: boolean }} props
 */
export function DistressBadgeRow({ signal, compact = false }) {
  if (!signal) return null;
  const items = [];
  if (signal.foreclosure) {
    items.push({ kind: "foreclosure", detail: signal.foreclosure_stage || "Distressed" });
  }
  if (signal.tax_delinquent) {
    const amt = signal.tax_delinquent_amount;
    items.push({ kind: "tax_delinquent", detail: amt ? `$${Math.round(amt).toLocaleString()}` : null });
  }
  if (signal.absentee) {
    items.push({ kind: "absentee" });
  }
  if (signal.vacant) {
    items.push({ kind: "vacant" });
  }
  if (signal.probate) {
    items.push({ kind: "probate" });
  }
  if (signal.tired_landlord) {
    items.push({ kind: "tired_landlord" });
  }
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="distress-badge-row">
      {items.map((it) => (
        <DistressBadge key={it.kind} kind={it.kind} detail={it.detail} compact={compact} />
      ))}
    </div>
  );
}
